// NextScene — Cast Talent (industry lead form).
// OTP-gated submission → POST /leads/cast-talent.
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { Logo } from "@/components/Logo";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldCheck, ArrowLeft } from "lucide-react";
import { COUNTRIES, DIAL_CODES, LANGUAGES } from "@/lib/countries";

const PROJECT_TYPES = ["Film","Web Series","OTT","TV","Ad Film","Music Video","YouTube","Brand Campaign","Event","Other"];
const CATEGORIES = ["Acting","Singing","Dancing","Modeling","Voice Artist","Comedy","Music","Anchoring","Photography","Filmmaking","Writing","Other"];
const GENDERS = ["Any","Male","Female","Other"];
const BUDGET_RANGES = ["Under ₹1 Lakh","₹1L – ₹5L","₹5L – ₹25L","₹25L – ₹1 Cr","₹1 Cr+","To be discussed"];

export default function CastTalent() {
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [dial, setDial] = useState("+91");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpToken, setOtpToken] = useState("");
  const [otpHint, setOtpHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Mandatory
  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("India");
  const [countryOther, setCountryOther] = useState("");
  // Optional
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [projectType, setProjectType] = useState("");
  const [projectTypeOther, setProjectTypeOther] = useState("");
  const [talents, setTalents] = useState([]);
  const [talentOther, setTalentOther] = useState("");
  const [gender, setGender] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [langs, setLangs] = useState([]);
  const [langOther, setLangOther] = useState("");
  const [shootLoc, setShootLoc] = useState("");
  const [shootStart, setShootStart] = useState("");
  const [shootEnd, setShootEnd] = useState("");
  const [budget, setBudget] = useState("");
  const [refLink, setRefLink] = useState("");
  const [requirement, setRequirement] = useState("");

  const canSendOtp = phone.length >= 6 && phone.length <= 15 && !busy;
  const mandatoryOk = fullName.trim() && company.trim() && city.trim() && country.trim() && (country !== "Other" || countryOther.trim());
  const canSubmit = otpVerified && mandatoryOk && !busy;

  const sendOtp = async () => {
    if (!canSendOtp) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/send-otp", { phone_number: `${dial}${phone}` });
      setOtpSent(true);
      setOtpHint(data.hint || "");
      toast.success(data.hint ? "OTP sent (MOCK)" : "OTP sent to your phone");
    } catch (err) { toast.error(errMsg(err, "Could not send OTP")); }
    finally { setBusy(false); }
  };

  const verifyOtpAndMark = async () => {
    const clean = String(otp).trim();
    if (clean.length !== 6) return toast.error("Enter the 6-digit OTP");
    setBusy(true);
    try {
      const { data } = await api.post("/leads/verify-otp", { phone_number: `${dial}${phone}`, code: clean });
      setOtpToken(data.verification_token);
      setOtpVerified(true);
      toast.success("Mobile number verified");
    } catch (err) { toast.error(errMsg(err, "Invalid or expired OTP")); }
    finally { setBusy(false); }
  };

  // Enter-key handler that focuses the next visible input inside the form
  const focusNext = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const form = e.currentTarget.closest("form");
    if (!form) return;
    const inputs = Array.from(form.querySelectorAll("input, select, textarea, button[type='button']"))
      .filter(el => !el.disabled && el.offsetParent !== null);
    const idx = inputs.indexOf(e.currentTarget);
    if (idx >= 0 && idx < inputs.length - 1) inputs[idx + 1].focus();
  };

  // "Other" chip-adder — adds trimmed value into the selected array as a
  // "Other: <value>" tag, clears the input, and lets the user add many.
  const addOtherChip = (raw, arr, setter, clear) => {
    const v = String(raw || "").trim();
    if (!v) return;
    const tag = `Other: ${v}`;
    if (!arr.includes(tag)) setter([...arr, tag]);
    clear("");
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/leads/cast-talent", {
        phone_number: `${dial}${phone}`, country_code: dial,
        otp_verification_token: otpToken,
        full_name: fullName.trim(), company: company.trim(),
        city: city.trim(), country: country.trim(), country_other: countryOther.trim(),
        website: website.trim(), email: email.trim(),
        project_type: projectType, project_type_other: projectTypeOther.trim(),
        talent_categories: talents, talent_category_other: talentOther.trim(),
        gender_preference: gender, age_range: ageRange.trim(),
        languages: langs, language_other: langOther.trim(),
        shoot_location: shootLoc.trim(),
        shoot_start: shootStart, shoot_end: shootEnd,
        budget_range: budget, reference_link: refLink.trim(),
        requirement: requirement.trim(),
      });
      setSubmitted(true);
    } catch (err) { toast.error(errMsg(err, "Could not submit — please try again")); }
    finally { setBusy(false); }
  };

  const toggle = (arr, setter, v) => setter(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

  return (
    <div style={{ backgroundColor: "#070709", minHeight: "100vh", color: "#F5F3EE" }}>
      <div className="max-w-5xl mx-auto px-5 md:px-8 pt-8 pb-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-[rgba(255,255,255,0.72)] hover:text-white"><ArrowLeft size={16}/> Back</Link>
        <Logo size="sm"/>
      </div>
      <div className="max-w-3xl mx-auto px-5 md:px-8 pb-24">
        {submitted ? (
          <SuccessCard heading="Your casting request has been submitted to the NextScene team." body="" nav={nav}/>
        ) : (
          <form onSubmit={(e)=>{ e.preventDefault(); if (canSubmit) submit(); }} onKeyDown={(e)=>{
              if (e.key === "Enter" && e.target.tagName !== "TEXTAREA" && e.target.type !== "submit") {
                e.preventDefault();
                focusNext(e);
              }
            }}>
            <h1 style={{ fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Bodoni Moda', 'Playfair Display', Georgia, serif", fontWeight: 400, fontSize: "clamp(40px, 5vw, 68px)", lineHeight: 1.02, letterSpacing: "-1.5px", margin: 0, color: "#F5F3EE" }}>
              Cast <span style={{ fontStyle: "italic", color: "#C79A2B" }}>Talent</span>
            </h1>
            <p style={{ marginTop: "14px", fontFamily: "'Inter', system-ui, sans-serif", fontSize: "16px", color: "rgba(255,255,255,0.72)", lineHeight: 1.6, maxWidth: "620px" }}>
              Submit your casting requirement and our team will help you discover suitable talent.
            </p>

            <div className="grid md:grid-cols-2" style={{ marginTop: "40px", gap: "18px" }}>
              <Field label="Full Name *"><input data-testid="ct-name" className="ns-form-input" value={fullName} onChange={(e)=>setFullName(e.target.value)} maxLength={120}/></Field>
              <Field label="Company / Agency *"><input data-testid="ct-company" className="ns-form-input" value={company} onChange={(e)=>setCompany(e.target.value)} maxLength={160}/></Field>
              <Field label="City *"><input data-testid="ct-city" className="ns-form-input" value={city} onChange={(e)=>setCity(e.target.value)} maxLength={80}/></Field>
              <Field label="Country *">
                <select data-testid="ct-country" className="ns-form-select" value={country} onChange={(e)=>{
                    const c = e.target.value;
                    setCountry(c);
                    // Auto-sync dial code when the country matches an entry in DIAL_CODES
                    const hit = DIAL_CODES.find(d => d.name === c);
                    if (hit) setDial(hit.code);
                  }}>
                  {COUNTRIES.map(c => <option key={c} value={c} style={{background:"#0B0B0D"}}>{c}</option>)}
                  <option value="Other" style={{background:"#0B0B0D"}}>Other</option>
                </select>
                {country === "Other" && (
                  <input className="ns-form-input" style={{marginTop:"10px"}} placeholder="Specify country" value={countryOther} onChange={(e)=>setCountryOther(e.target.value)} maxLength={120}/>
                )}
              </Field>
            </div>

            {/* Mobile + OTP */}
            <div style={{ marginTop: "18px" }}>
              <label className="ns-form-label">Mobile Number *</label>
              <div className="flex items-stretch" style={{ gap: "10px", flexWrap: "wrap" }}>
                <select value={dial} onChange={(e)=>setDial(e.target.value)} disabled={otpVerified} data-testid="ct-dial"
                  className="ns-form-select" style={{ width: "auto", minWidth: "150px", flexShrink: 0 }}>
                  {DIAL_CODES.map(d => (
                    <option key={`${d.name}-${d.code}`} value={d.code} style={{background:"#0B0B0D"}}>
                      {d.flag} {d.name} {d.code}
                    </option>
                  ))}
                </select>
                <input type="tel" inputMode="numeric" maxLength={15} className="ns-form-input" data-testid="ct-phone" style={{ flex: 1, minWidth: "180px" }}
                  placeholder="Mobile number" value={phone} onChange={(e)=>setPhone(e.target.value.replace(/\D/g,""))} disabled={otpVerified}/>
                <button type="button" onClick={sendOtp} disabled={!canSendOtp || otpVerified} data-testid="ct-send-otp"
                  style={{ borderRadius: "14px", padding: "0 18px", background: canSendOtp && !otpVerified ? "#C79A2B" : "rgba(199,154,43,0.35)", color: "#070709", fontWeight: 600, fontFamily: "'Inter'", fontSize: "14px", border: "none", cursor: canSendOtp && !otpVerified ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}>
                  {otpSent ? "Resend OTP" : "Send OTP"}
                </button>
              </div>
              {otpSent && !otpVerified && (
                <div className="flex items-stretch" style={{ marginTop: "10px", gap: "10px" }}>
                  <input type="tel" inputMode="numeric" maxLength={6} className="ns-form-input" data-testid="ct-otp"
                    placeholder="6-digit code" value={otp} onChange={(e)=>setOtp(e.target.value.replace(/\D/g,""))}
                    style={{ letterSpacing: "0.35em", textAlign: "center", fontSize: "18px" }}/>
                  <button type="button" onClick={verifyOtpAndMark} data-testid="ct-verify-otp"
                    style={{ borderRadius: "14px", padding: "0 18px", background: "transparent", color: "#C79A2B", fontWeight: 600, fontFamily: "'Inter'", fontSize: "14px", border: "1px solid #C79A2B", cursor: "pointer", whiteSpace: "nowrap" }}>
                    Verify
                  </button>
                </div>
              )}
              {otpHint && !otpVerified && (
                <p style={{ marginTop: "6px", fontSize: "12px", color: "#F4C550" }}>MOCK: use {otpHint.replace(/^MOCK OTP:\s*/, "")}</p>
              )}
              {otpVerified && (
                <p style={{ marginTop: "8px", fontSize: "13px", color: "#C79A2B", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <CheckCircle2 size={14}/> Mobile verified
                </p>
              )}
            </div>

            <div className="grid md:grid-cols-2" style={{ marginTop: "18px", gap: "18px" }}>
              <Field label="Company Website / URL"><input className="ns-form-input" value={website} onChange={(e)=>setWebsite(e.target.value)} maxLength={300}/></Field>
              <Field label="Email Address"><input type="email" className="ns-form-input" value={email} onChange={(e)=>setEmail(e.target.value)} maxLength={200}/></Field>
              <Field label="Project Type">
                <select className="ns-form-select" value={projectType} onChange={(e)=>setProjectType(e.target.value)}>
                  <option value="" style={{background:"#0B0B0D"}}>Select</option>
                  {PROJECT_TYPES.map(p => <option key={p} value={p} style={{background:"#0B0B0D"}}>{p}</option>)}
                </select>
              </Field>
              {projectType === "Other" && <Field label="Specify Project Type"><input className="ns-form-input" value={projectTypeOther} onChange={(e)=>setProjectTypeOther(e.target.value)} maxLength={120}/></Field>}
              <Field label="Gender Preference">
                <select className="ns-form-select" value={gender} onChange={(e)=>setGender(e.target.value)}>
                  <option value="" style={{background:"#0B0B0D"}}>Select</option>
                  {GENDERS.map(g => <option key={g} value={g} style={{background:"#0B0B0D"}}>{g}</option>)}
                </select>
              </Field>
              <Field label="Age Range"><input className="ns-form-input" placeholder="e.g., 25 – 35" value={ageRange} onChange={(e)=>setAgeRange(e.target.value)} maxLength={40}/></Field>
              <Field label="Shooting Location"><input className="ns-form-input" value={shootLoc} onChange={(e)=>setShootLoc(e.target.value)} maxLength={120}/></Field>
              <Field label="Budget Range">
                <select className="ns-form-select" value={budget} onChange={(e)=>setBudget(e.target.value)}>
                  <option value="" style={{background:"#0B0B0D"}}>Select</option>
                  {BUDGET_RANGES.map(b => <option key={b} value={b} style={{background:"#0B0B0D"}}>{b}</option>)}
                </select>
              </Field>
              <Field label="Shoot Start"><input type="date" className="ns-form-input" value={shootStart} onChange={(e)=>setShootStart(e.target.value)}/></Field>
              <Field label="Shoot End"><input type="date" className="ns-form-input" value={shootEnd} onChange={(e)=>setShootEnd(e.target.value)}/></Field>
              <Field label="Reference Link / Moodboard" full><input className="ns-form-input" value={refLink} onChange={(e)=>setRefLink(e.target.value)} maxLength={400}/></Field>
            </div>

            <div style={{ marginTop: "22px" }}>
              <label className="ns-form-label">Talent Category Needed</label>
              <div className="flex flex-wrap" style={{ gap: "8px" }}>
                {CATEGORIES.map(c => (
                  <button key={c} type="button" className={`ns-form-chip ${talents.includes(c) ? "is-on" : ""}`} onClick={()=>toggle(talents, setTalents, c)}>{c}</button>
                ))}
              </div>
              {talents.includes("Other") && (
                <div style={{ marginTop:"10px", display:"flex", flexDirection:"column", gap:"8px" }}>
                  <div className="flex" style={{ gap:"8px" }}>
                    <input className="ns-form-input" placeholder="Specify other category — press Enter to add" value={talentOther}
                      onChange={(e)=>setTalentOther(e.target.value)}
                      onKeyDown={(e)=>{ if (e.key === "Enter") { e.preventDefault(); addOtherChip(talentOther, talents, setTalents, setTalentOther); } }}
                      maxLength={120}/>
                    <button type="button" onClick={()=>addOtherChip(talentOther, talents, setTalents, setTalentOther)}
                      style={{ padding:"0 18px", borderRadius:"14px", background:"transparent", color:"#C79A2B", border:"1px solid #C79A2B", fontFamily:"'Inter'", fontSize:"13px", cursor:"pointer" }}>Add</button>
                  </div>
                  <div className="flex flex-wrap" style={{ gap:"8px" }}>
                    {talents.filter(t => t.startsWith("Other: ")).map(t => (
                      <span key={t} className="ns-form-chip is-on" style={{ gap:"6px" }}>
                        {t.replace(/^Other: /, "")}
                        <button type="button" onClick={()=>setTalents(talents.filter(x => x !== t))} style={{ background:"transparent", border:"none", color:"#F5F3EE", cursor:"pointer", fontSize:"14px", lineHeight:1 }} aria-label="Remove">×</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: "22px" }}>
              <label className="ns-form-label">Languages Required</label>
              <div className="flex flex-wrap" style={{ gap: "8px" }}>
                {LANGUAGES.map(l => (
                  <button key={l} type="button" className={`ns-form-chip ${langs.includes(l) ? "is-on" : ""}`} onClick={()=>toggle(langs, setLangs, l)}>{l}</button>
                ))}
                <button type="button" className={`ns-form-chip ${langs.includes("Other") ? "is-on" : ""}`} onClick={()=>toggle(langs, setLangs, "Other")}>Other</button>
              </div>
              {langs.includes("Other") && (
                <div style={{ marginTop:"10px", display:"flex", flexDirection:"column", gap:"8px" }}>
                  <div className="flex" style={{ gap:"8px" }}>
                    <input className="ns-form-input" placeholder="Specify other language — press Enter to add" value={langOther}
                      onChange={(e)=>setLangOther(e.target.value)}
                      onKeyDown={(e)=>{ if (e.key === "Enter") { e.preventDefault(); addOtherChip(langOther, langs, setLangs, setLangOther); } }}
                      maxLength={120}/>
                    <button type="button" onClick={()=>addOtherChip(langOther, langs, setLangs, setLangOther)}
                      style={{ padding:"0 18px", borderRadius:"14px", background:"transparent", color:"#C79A2B", border:"1px solid #C79A2B", fontFamily:"'Inter'", fontSize:"13px", cursor:"pointer" }}>Add</button>
                  </div>
                  <div className="flex flex-wrap" style={{ gap:"8px" }}>
                    {langs.filter(t => t.startsWith("Other: ")).map(t => (
                      <span key={t} className="ns-form-chip is-on" style={{ gap:"6px" }}>
                        {t.replace(/^Other: /, "")}
                        <button type="button" onClick={()=>setLangs(langs.filter(x => x !== t))} style={{ background:"transparent", border:"none", color:"#F5F3EE", cursor:"pointer", fontSize:"14px", lineHeight:1 }} aria-label="Remove">×</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginTop: "22px" }}>
              <Field label="Detailed Requirement"><textarea className="ns-form-textarea" value={requirement} onChange={(e)=>setRequirement(e.target.value)} maxLength={4000}/></Field>
            </div>

            <button type="button" onClick={submit} disabled={!canSubmit} data-testid="ct-submit"
              style={{ marginTop: "28px", width: "100%", height: "58px", borderRadius: "999px",
                background: canSubmit ? "#C79A2B" : "rgba(199,154,43,0.35)",
                color: "#070709", fontFamily: "'Inter'", fontWeight: 600, fontSize: "16px",
                border: "none", cursor: canSubmit ? "pointer" : "not-allowed",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "10px",
                boxShadow: canSubmit ? "0 10px 30px rgba(199,154,43,0.28), 0 0 28px rgba(199,154,43,0.18)" : "none" }}>
              {busy ? <Loader2 size={18} className="animate-spin"/> : <ShieldCheck size={18}/>}
              Submit Casting Requirement
            </button>
            {!otpVerified && (
              <p style={{ marginTop: "10px", textAlign: "center", fontSize: "12px", color: "rgba(255,255,255,0.6)", fontFamily: "'Inter'" }}>
                Verify your mobile number to enable submission.
              </p>
            )}
          </form>
        )}
      </div>
      <Footer/>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : undefined }}>
      <label className="ns-form-label">{label}</label>
      {children}
    </div>
  );
}

function SuccessCard({ heading, body, nav }) {
  return (
    <div style={{ marginTop: "80px", padding: "48px 36px", borderRadius: "28px", border: "1px solid rgba(199,154,43,0.32)", background: "linear-gradient(180deg, rgba(199,154,43,0.06) 0%, rgba(7,7,9,0) 100%)", textAlign: "center" }}>
      <div style={{ width: "56px", height: "56px", margin: "0 auto 18px", borderRadius: "999px", border: "1px solid #C79A2B", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
        <CheckCircle2 size={26} color="#C79A2B"/>
      </div>
      <h2 style={{ fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Playfair Display', Georgia, serif", fontWeight: 400, fontSize: "36px", color: "#F5F3EE", margin: 0, letterSpacing: "-1px" }}>{heading}</h2>
      <p style={{ marginTop: "12px", fontFamily: "'Inter'", fontSize: "15px", color: "rgba(255,255,255,0.78)", lineHeight: 1.6 }}>{body}</p>
      <button type="button" onClick={() => nav("/")} style={{ marginTop: "22px", height: "44px", padding: "0 22px", borderRadius: "999px", background: "transparent", color: "#F5F3EE", fontFamily: "'Inter'", fontWeight: 500, fontSize: "14px", border: "1px solid rgba(199,154,43,0.55)", cursor: "pointer" }}>
        Back to Home
      </button>
    </div>
  );
}
